import { Box, ButtonBase, alpha, styled } from '@mui/material';
import type { GetStoreItemsSub } from 'src/openapi';
import { GOLD_ACCENT } from 'src/components/buyCreditsDialog/styles/index.styles';
import { GOLD_DEEP, bodyText } from './sheet.styles';
import type { ProTier, ProTierType } from './tiers';

const List = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
});

const Option = styled(ButtonBase, {
  shouldForwardProp: (p) => p !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '9px 12px',
  borderRadius: 12,
  textAlign: 'left',
  border: `1.5px solid ${
    selected ? GOLD_DEEP : alpha(theme.palette.primary.main, 0.14)
  }`,
  backgroundColor: selected ? alpha(GOLD_ACCENT, 0.14) : 'transparent',
  '&.Mui-focusVisible': { outline: `2px solid ${GOLD_DEEP}` },
}));

const Dot = styled(Box, {
  shouldForwardProp: (p) => p !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
  width: 16,
  height: 16,
  flexShrink: 0,
  borderRadius: '50%',
  border: `2px solid ${
    selected ? GOLD_DEEP : alpha(theme.palette.text.primary, 0.35)
  }`,
  boxShadow: selected
    ? `inset 0 0 0 3px ${theme.palette.background.paper}`
    : 'none',
  backgroundColor: selected ? GOLD_DEEP : 'transparent',
}));

const Name = styled(Box)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

const Detail = styled(Box)(({ theme }) => ({
  fontSize: 12,
  lineHeight: 1.35,
  ...bodyText(theme),
}));

const Price = styled(Box)(({ theme }) => ({
  marginLeft: 'auto',
  fontSize: 14,
  fontWeight: 800,
  whiteSpace: 'nowrap',
  color: theme.palette.text.primary,
  '& span': { fontSize: 11.5, fontWeight: 500, ...bodyText(theme) },
  '& s': {
    marginRight: 6,
    fontSize: 12,
    fontWeight: 500,
    ...bodyText(theme),
  },
}));

/** What sets a tier apart, in one line. */
export const tierDetail = (tier: ProTier) =>
  `${tier.aiCredits.toLocaleString('en-IN')} AI credits · ${
    tier.monthlyCall ? 'free 1:1 call monthly' : 'no free 1:1 call'
  }`;

interface TierPickerProps {
  options: { tier: ProTier; item: GetStoreItemsSub }[];
  selected: ProTierType;
  onSelect: (type: ProTierType) => void;
  /** Strike each tier's list price beside its price, where it has one. */
  showListPrice?: boolean;
}

/** The Pro tiers on sale, one to pick: name, what sets it apart, its price. */
const TierPicker = ({
  options,
  selected,
  onSelect,
  showListPrice = false,
}: TierPickerProps) => (
  <List role="radiogroup" aria-label="Pro options">
    {options.map(({ tier, item }) => {
      const on = tier.type === selected;
      return (
        <Option
          key={tier.type}
          role="radio"
          aria-checked={on}
          selected={on}
          onClick={() => onSelect(tier.type)}
        >
          <Dot selected={on} />
          <Box>
            <Name>{tier.name}</Name>
            <Detail>{tierDetail(tier)}</Detail>
          </Box>
          <Price>
            {showListPrice &&
              Number(item.gross_price_in_currency ?? 0) >
                Number(item.gross_price_in_currency_after_disc ?? 0) && (
                <s>₹{Number(item.gross_price_in_currency)}</s>
              )}
            ₹{Number(item.gross_price_in_currency_after_disc ?? 0)}
            <span>/mo</span>
          </Price>
        </Option>
      );
    })}
  </List>
);

export default TierPicker;
